import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';
import MenuItem from '@mui/material/MenuItem';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LogoutIcon from '@mui/icons-material/Logout';
import { createRef, useEffect, useRef, useState } from 'react';
import { fromAccounts, fromAuth, useAppDispatch } from '../../../store';
import { useNavigate, useQuery } from '../../../routes';
import CurrentUserInfo from './CurrentUserInfo';
import { SIDEBAR_WIDTH } from '../../../utils/Constants';
import { AccountItem } from './AccountItem';
import { useAccount } from '../../../account';
import { Account } from '../../../models';

const useStyles = makeStyles(theme => ({
  menuItems: {
    maxHeight: 250,
    overflow: 'auto',
    padding: theme.spacing(1, 0),
  },
  menuItem: {
    padding: theme.spacing(0, 2),
  },
  userBox: {
    width: '100%',
    padding: theme.spacing(0),
  },
  userAvatar: {
    height: 32,
    width: 32,
    margin: theme.spacing(1, 1, 1, 0),
  },
}));

const StyledPopper = styled(Popper)(
  () => ({
    zIndex: 1300,
    border: '1px solid #e0e0e0',
    borderRadius: 0,
    '& .MuiPaper-root': {
      borderRadius: 0,
    },
  }),
);

interface SelectAccountMenuProps {
  open: boolean;
  expanded: boolean;
  anchorEl: HTMLButtonElement | null;
  closeMenu: () => void;
}

const SelectAccountMenu = (
  {
    open,
    expanded,
    closeMenu,
    anchorEl,
  } : SelectAccountMenuProps,
) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const { queryDictionary } = useQuery();
  const { accounts, loading } = useAccount();
  const currentPath = useLocation().pathname;
  const { replace, replaceQuery } = useNavigate();
  const [allowedAccount, setAllowedAccount] = useState<Account[]>([]);
  const [showAccountMenu, setShowAccountMenu] = useState(true);
  const accountRefs = useRef<any[]>([]);
  accountRefs.current = accounts.map((_, index) => accountRefs.current[index] ?? createRef());
  const handleSwitchAccount = (account: Account) => () => {
    dispatch(fromAccounts.doSetSelectedAccount(account));
    replace(currentPath, replaceQuery({ ...queryDictionary(), accountId: account.id }));
    closeMenu();
  };

  useEffect(() => {
    if (!loading) {
      setAllowedAccount(accounts);
    }
  }, [loading, accounts]);

  useEffect(() => {
    if (accountRefs.current && accountRefs.current.length > 0) {
      // skip when there's account is not finish checking permission,
      if (accountRefs.current.some(ref => ref.current === null || ref.current.loading !== false)) {
        return;
      }

      // filter out the account that is not allowed
      const filteredAccount = allowedAccount
        .filter((_, index) => accountRefs.current[index].current.allowed);
      setAllowedAccount(filteredAccount);
      // after check, only show account when there's more than one account
      setShowAccountMenu(filteredAccount.length > 0);
    }
  }, [allowedAccount, accountRefs]);

  const renderMenuItem = () => (
    <Box id="sidebar-user-menu" display="flex" flexDirection="column">
      {!open && <CurrentUserInfo open={open} />}
      <Divider />
      {showAccountMenu && (
        <Box
          id="sidebar-user-menu-switch-account"
          display="flex"
          flexDirection="column"
        >
          <div id="sidebar-user-menu-switch-account-label">
            <Typography
              variant="body1"
              fontWeight={700}
              fontSize={14}
              sx={{
                paddingX: 2.5,
                paddingY: 1.5,
              }}
            >
              <FormattedMessage id="layout.sidebar.user.switch.account.label" />
            </Typography>
          </div>
          <Divider />
          <Box id="sidebar-user-menu-account-list" className={classes.menuItems}>
            {allowedAccount
              .map((account, index) => (
                <AccountItem
                  account={account}
                  open={open}
                  ref={accountRefs.current[index]}
                  handleSwitchAccount={handleSwitchAccount}
                />
              ))}
          </Box>
          <Divider />
        </Box>
      )}
      <Box
        id="sidebar-user-menu-logout"
        display="flex"
        flexDirection="column"
        className={classes.menuItems}
      >
        <MenuItem
          onClick={() => dispatch(fromAuth.doLogout({
            redirectUrl: `${window.location.origin}/logout`,
          }))}
          sx={{ paddingY: 2, paddingX: 4 }}
        >
          <LogoutIcon />
          <Typography
            variant="body1"
            fontSize={14}
            fontWeight={700}
          >
            <FormattedMessage id="layout.sidebar.user.logout" />
          </Typography>
        </MenuItem>
      </Box>
    </Box>
  );

  return (
    <StyledPopper
      open={expanded}
      anchorEl={anchorEl}
      role={undefined}
      transition
      disablePortal={open}
      placement={open ? 'bottom-end' : 'right-start'}
      modifiers={[
        { name: 'preventOverflow', enabled: false },
        { name: 'flip', enabled: false },
      ]}
    >
      {({ TransitionProps }) => (
        <Fade
          {...TransitionProps}
        >
          <Paper>
            <ClickAwayListener onClickAway={() => closeMenu()}>
              <MenuList
                sx={{
                  overflow: 'auto',
                  width: SIDEBAR_WIDTH,
                  maxHeight: 500,
                  padding: 0,
                }}
              >
                {renderMenuItem()}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Fade>
      )}
    </StyledPopper>
  );
};

export default SelectAccountMenu;
